import React, { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Box, Text } from '@chakra-ui/react';
import FavoriteSection from './FavoriteSection';
import ToolGrid from './ToolGrid';
import { getAvailableTools } from './appRegistry';
import { LAUNCHER } from './launcherVisualTokens';

/** Space from viewport edges — tight so the panel can use almost full width. */
const PANEL_MARGIN_RIGHT = 4;
const PANEL_MARGIN_LEFT = 4;
/** Horizontal gap between favorite tiles (Chakra `gap={2}` ≈ 8px). */
const FAVORITE_TILE_GAP_PX = 8;
/** Extra horizontal padding: favorites surface + panel inner (approx). */
const PANEL_H_PADDING_EXTRA = 56;
/** Space between launcher button bottom and panel top (Google-like tight attach). */
const GAP_BELOW_TRIGGER = 6;
const AppLauncherPanel = ({
  isOpen,
  onClose,
  anchorRef,
  favoriteApps,
  ids,
  streamPlayerUnlocked,
  favoriteIdSet,
  isFull,
  onReorder,
  onNavigate,
  removeFromFavorite,
  addToFavorite,
}) => {
  const panelRef = useRef(null);
  const [layout, setLayout] = useState({
    top: 72,
    right: PANEL_MARGIN_RIGHT,
    maxWidth: 360,
    minWidth: 320,
  });

  const availableTools = useMemo(
    () => getAvailableTools(favoriteIdSet, streamPlayerUnlocked),
    [favoriteIdSet, streamPlayerUnlocked],
  );

  useLayoutEffect(() => {
    if (!isOpen) return;
    const update = () => {
      const el = anchorRef?.current;
      if (!el || typeof window === 'undefined') return;
      const r = el.getBoundingClientRect();
      const vw = window.innerWidth;

      const right = PANEL_MARGIN_RIGHT;

      const maxWidth = vw - PANEL_MARGIN_LEFT - PANEL_MARGIN_RIGHT;

      const tw = LAUNCHER.appTileWidthPx;
      const favoritesRowMin = 4 * tw + 3 * FAVORITE_TILE_GAP_PX;
      const minWidth = Math.min(
        favoritesRowMin + PANEL_H_PADDING_EXTRA,
        maxWidth,
      );

      setLayout({
        top: r.bottom + GAP_BELOW_TRIGGER,
        right,
        maxWidth,
        minWidth,
      });
    };
    update();
    window.addEventListener('resize', update);
    return () => {
      window.removeEventListener('resize', update);
    };
  }, [isOpen, anchorRef, favoriteApps.length, availableTools.length, ids.length]);

  useEffect(() => {
    if (!isOpen) return;
    const onPointerDown = (e) => {
      const t = e.target;
      if (panelRef.current?.contains(t)) return;
      if (anchorRef?.current?.contains(t)) return;
      onClose();
    };
    const onKey = (e) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('mousedown', onPointerDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onPointerDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [isOpen, onClose, anchorRef]);

  useEffect(() => {
    if (!isOpen) return;
    const id = window.requestAnimationFrame(() => {
      /* Focus the shell, not the first tile — avoids “stuck” favorite chrome on tile 1. */
      panelRef.current?.focus();
    });
    return () => window.cancelAnimationFrame(id);
  }, [isOpen]);

  const maxH =
    typeof window !== 'undefined'
      ? `min(calc(100vh - ${layout.top + PANEL_MARGIN_RIGHT}px), 74vh)`
      : '74vh';

  const node = (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          key="vorakit-launcher"
          role="dialog"
          aria-modal="true"
          aria-label="VoraKit tools"
          initial={{ opacity: 0, scale: 0.97, y: -4 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          exit={{ opacity: 0, scale: 0.97, y: -4 }}
          transition={{ duration: 0.18, ease: [0.16, 1, 0.3, 1] }}
          style={{
            position: 'fixed',
            top: layout.top,
            right: layout.right,
            width: 'max-content',
            minWidth: layout.minWidth,
            maxWidth: layout.maxWidth,
            zIndex: 10000,
            maxHeight: maxH,
            transformOrigin: 'top right',
          }}
        >
          <Box
            ref={panelRef}
            tabIndex={-1}
            borderRadius={LAUNCHER.panelRadius}
            bg={LAUNCHER.panelBg}
            backdropFilter={
              LAUNCHER.panelBackdropBlur
                ? `blur(${LAUNCHER.panelBackdropBlur})`
                : undefined
            }
            boxShadow={LAUNCHER.panelShadow}
            overflow="hidden"
            display="inline-flex"
            flexDirection="column"
            maxW="100%"
            w="max-content"
            maxH="inherit"
            _focusVisible={{
              outline: 'none',
              boxShadow: `${LAUNCHER.panelShadow}, 0 0 0 2px rgba(45, 212, 191, 0.65)`,
            }}
          >
            <Box
              px={2.5}
              pt={2.5}
              pb={3}
              overflowY="auto"
              overflowX="hidden"
              maxH="inherit"
              w="max-content"
              maxW="100%"
              sx={{
                scrollbarWidth: 'thin',
                scrollbarColor: 'rgba(255, 255, 255, 0.22) transparent',
                '&::-webkit-scrollbar': { width: '8px' },
                '&::-webkit-scrollbar-thumb': {
                  borderRadius: 'full',
                  background: 'rgba(255, 255, 255, 0.18)',
                },
              }}
            >
              <Box
                borderRadius={LAUNCHER.favoritesSurfaceRadius}
                px={LAUNCHER.favoritesPadding}
                pt={3}
                pb={3}
                bg={LAUNCHER.favoritesSurfaceBg}
                boxShadow={LAUNCHER.favoritesSurfaceShadow}
                w="max-content"
                maxW="100%"
              >
                <Text as="h2" {...LAUNCHER.titleFavorites} mb={3}>
                  Favorites
                </Text>

                <FavoriteSection
                  ids={ids}
                  favoriteApps={favoriteApps}
                  onReorder={onReorder}
                  onNavigate={onNavigate}
                  onRemove={removeFromFavorite}
                />
              </Box>

              <Box mt={5} w="max-content" maxW="100%">
                <Text as="h2" {...LAUNCHER.titleAllTools} mb={3}>
                  All tools
                </Text>

                {availableTools.length === 0 ? (
                  <Text {...LAUNCHER.bodyMuted} textAlign="left" py={1}>
                    All tools are in Favorites.
                  </Text>
                ) : (
                  <ToolGrid
                    apps={availableTools}
                    onOpen={onNavigate}
                    canAddMore={!isFull}
                    onAdd={addToFavorite}
                  />
                )}
              </Box>
            </Box>
          </Box>
        </motion.div>
      )}
    </AnimatePresence>
  );

  if (typeof document === 'undefined') return null;
  return createPortal(node, document.body);
};

export default AppLauncherPanel;
